import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Gem, Coins, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { shareWinCard } from "@/lib/shareCard";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

function fireConfetti() {
  const colors = ["#fbbf24", "#f472b6", "#a78bfa", "#67e8f9", "#ffffff"];
  const end = Date.now() + 2500;
  (function frame() {
    confetti({ particleCount: 6, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors, zIndex: 10000 });
    confetti({ particleCount: 6, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors, zIndex: 10000 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  setTimeout(() => confetti({ particleCount: 160, spread: 120, startVelocity: 45, origin: { y: 0.5 }, colors, zIndex: 10000, scalar: 1.2 }), 300);
}

export default function DiamondCelebration({ show, bonus, onClaimed }) {
  const { refresh } = useAuth();
  const { t } = useT();
  const [state, setState] = useState("idle"); // idle | claiming | done
  const [reward, setReward] = useState(bonus);
  const fired = useRef(false);

  useEffect(() => {
    if (show && !fired.current) { fired.current = true; fireConfetti(); }
  }, [show]);

  const claim = async () => {
    setState("claiming");
    try {
      const { data } = await api.post("/loyalty/diamond-bonus");
      setReward(data.reward);
      setState("done");
      confetti({ particleCount: 80, spread: 90, origin: { y: 0.6 }, colors: ["#fbbf24", "#fde68a"], zIndex: 10000 });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim bonus");
      setState("idle");
    }
  };

  const close = () => { onClaimed?.(); };

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div data-testid="diamond-celebration" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] overflow-y-auto bg-black/80 backdrop-blur-md">
          <div className="min-h-full flex items-center justify-center p-4 py-8">
          <motion.div initial={{ scale: 0.7, y: 40, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="relative w-full max-w-md rounded-3xl border border-amber-400/50 bg-[#0d0b14] p-6 sm:p-8 text-center shadow-[0_0_80px_rgba(251,191,36,0.35)] overflow-hidden">
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-gradient-to-br from-amber-400/40 via-fuchsia-500/30 to-cyan-400/20 blur-3xl" />
            <motion.div animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
              className="relative mx-auto mb-5 w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-300 via-fuchsia-400 to-cyan-300 flex items-center justify-center shadow-[0_0_40px_rgba(244,114,182,0.6)]">
              <Gem className="w-12 h-12 text-[#0d0b14]" strokeWidth={2.5} />
            </motion.div>
            <p className="relative text-xs font-bold tracking-[0.3em] text-amber-300 uppercase flex items-center justify-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> {t("diamond_unlocked")} <Sparkles className="w-3.5 h-3.5" />
            </p>
            <h2 data-testid="diamond-celebration-title" className="relative mt-2 font-display font-black text-3xl sm:text-4xl bg-clip-text text-transparent bg-gradient-to-r from-amber-200 via-fuchsia-300 to-cyan-200">
              {t("diamond_title")}
            </h2>
            <p className="relative mt-3 text-sm text-gray-300">{t("diamond_sub")}</p>

            <div className="relative mt-6 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-5 py-4">
              <p className="text-[11px] uppercase tracking-widest text-amber-200/80 font-semibold">{state === "done" ? t("diamond_bonus_added") : t("diamond_bonus_label")}</p>
              <p data-testid="diamond-bonus-amount" className="mt-1 font-mono font-black text-3xl text-amber-300 flex items-center justify-center gap-2">
                <Coins className="w-7 h-7" /> +{Number(reward ?? bonus ?? 0).toLocaleString()}
              </p>
              <p className="mt-1 text-[11px] text-gray-400">{t("diamond_coins_note")}</p>
            </div>

            <ul className="relative mt-5 grid grid-cols-2 gap-2 text-left text-xs text-gray-300">
              {[t("diamond_perk_1"), t("diamond_perk_2"), t("diamond_perk_3"), t("diamond_perk_4")].map((p) => (
                <li key={p} className="flex items-start gap-1.5 rounded-lg bg-white/5 px-2.5 py-2"><Gem className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" /> {p}</li>
              ))}
            </ul>

            {state !== "done" ? (
              <button data-testid="diamond-claim-button" onClick={claim} disabled={state === "claiming"}
                className="relative mt-6 w-full py-3.5 rounded-full font-bold text-[#0d0b14] bg-gradient-to-r from-amber-300 via-amber-400 to-fuchsia-400 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200 disabled:opacity-60">
                {state === "claiming" ? t("loading") : t("diamond_claim")}
              </button>
            ) : (
              <>
              <button data-testid="diamond-share-button" onClick={() => shareWinCard({ score: "VIP", game: "I MADE DIAMOND", unit: "LOYALTY TIER", subtitle: "Diamond VIP · PhonePlay", accent: "#fbbf24", name: "Diamond", caption: "I just made Diamond VIP on PhonePlay 💎 Where Skill Wins — phoneplay.net" })}
                className="relative mt-6 w-full py-3 rounded-full font-semibold text-amber-200 border border-amber-400/50 bg-amber-500/10 hover:bg-amber-500/20 active:scale-[0.98] transition-[transform,background-color] duration-200 flex items-center justify-center gap-2">
                <Share2 className="w-4 h-4" /> {t("diamond_share")}
              </button>
              <button data-testid="diamond-enter-lounge" onClick={close}
                className="relative mt-3 w-full py-3.5 rounded-full font-bold text-[#0d0b14] bg-gradient-to-r from-amber-300 to-cyan-300 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200">
                {t("diamond_enter_lounge")}
              </button>
              </>
            )}
          </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
